import {
  Table,
  Thead,
  Td,
  Th,
  Tr,
  Tbody,
  Box,
  Flex,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
} from '@chakra-ui/react';
import {
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  useReactTable,
  SortingState,
  Row,
} from '@tanstack/react-table';
import React from 'react';

import { utils as commonUtils } from '@finley-technologies/common';
import { CreditAgreement } from '@finley-technologies/finley-types';

import { CreditFacilityGQLType } from 'types/dashboard-core-gqlTypes.generated';

import {
  fixedColumnsStyleToColumnIndexMap,
  PortfolioRiskTableRow,
  portfolioRiskTableFacilityColumnId,
} from './PortfolioRiskTableUtils';
import { CalculationTerminologiesPopover } from '~/core/routes/calculation-terminologies/CalculationTerminologiesPopover';
import { colors } from '~/packages/common-frontend/colors';
import { Icon } from '~/packages/common-frontend/components/icons';
import { Typography } from '~/packages/common-frontend/components/Typography';

type StickyTdProps = {
  children: React.ReactNode;
  cellIndex: number;
};
const StickyTd = ({ children, cellIndex }: StickyTdProps) => {
  return (
    <Td
      cursor="default"
      position="sticky"
      backgroundColor="white"
      style={{
        ...fixedColumnsStyleToColumnIndexMap[cellIndex],
      }}
      fontSize="sm"
      padding="16px"
    >
      {children}
    </Td>
  );
};

type NonStickyTdProps = { children: React.ReactNode };
const NonStickyTd = ({ children }: NonStickyTdProps) => {
  return (
    <Td cursor="default" fontSize="sm" padding="16px">
      {children}
    </Td>
  );
};

type TableTdProps = {
  children: React.ReactNode;
  cellIndex: number;
};
const TableTd = ({ children, cellIndex }: TableTdProps) => {
  return fixedColumnsStyleToColumnIndexMap[cellIndex] ? (
    <StickyTd cellIndex={cellIndex}>{children}</StickyTd>
  ) : (
    <NonStickyTd>{children}</NonStickyTd>
  );
};

type TableRowProps = { row: Row<PortfolioRiskTableRow> };
const TableRow = ({ row }: TableRowProps) => {
  return (
    <Tr key={row.id}>
      {row.getVisibleCells().map((cell, index) => (
        <TableTd key={cell.id} cellIndex={index}>
          {(() => {
            const cellValue = cell.getValue();
            if (
              cell.column.id === portfolioRiskTableFacilityColumnId &&
              typeof cellValue === 'string'
            ) {
              return flexRender(
                cell.row.original.creditFacilityName,
                cell.getContext(),
              );
            }

            if (typeof cellValue === 'number') {
              return flexRender(
                commonUtils.numberToFormattedString(
                  cellValue,
                  'percentage',
                  false,
                ),
                cell.getContext(),
              );
            }

            return flexRender(
              cellValue ? String(cellValue) : null,
              cell.getContext(),
            );
          })()}
        </TableTd>
      ))}
    </Tr>
  );
};

type PortfolioRiskTableTemplateProps = {
  portfolioRiskTableRows: PortfolioRiskTableRow[];
  columns: {
    accessorKey: string;
    header: string;
  }[];
  maxColumnCount: number;
  tableTitle: string;
  creditAgreementArtifacts?:
    | CreditAgreement.DomainEntity.CreditAgreementFullArtifact[]
    | null;
  creditFacilities?:
    | Pick<CreditFacilityGQLType, 'id' | 'slug' | 'name'>[]
    | null;
};
export const PortfolioRiskTableTemplate = ({
  portfolioRiskTableRows,
  columns,
  maxColumnCount,
  tableTitle,
  creditAgreementArtifacts,
  creditFacilities,
}: PortfolioRiskTableTemplateProps) => {
  const [sorting, setSorting] = React.useState<SortingState>([]);

  const table = useReactTable({
    data: portfolioRiskTableRows,
    columns,
    state: {
      sorting,
    },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  return (
    <Flex direction="column" gap="1.5rem">
      <Flex alignItems="center" gap="0.5rem">
        <Typography variant="section-subtitle" trim>
          {tableTitle}
        </Typography>
        <CalculationTerminologiesPopover
          termKeyLabel={tableTitle}
          creditAgreementArtifacts={creditAgreementArtifacts}
          creditFacilities={creditFacilities}
        />
      </Flex>

      {columns.length > maxColumnCount && (
        <Alert status="warning">
          <AlertIcon />
          <Flex direction="row" flexWrap="wrap">
            <AlertTitle>Large dataset.</AlertTitle>
            <AlertDescription>
              To improve performance, only the latest {maxColumnCount} columns
              are shown.
            </AlertDescription>
          </Flex>
        </Alert>
      )}
      <Box
        borderWidth="1px"
        borderColor={colors.gray[200]}
        overflowX="auto"
        overflowY="auto"
        position="relative"
      >
        <Table style={{ borderCollapse: 'separate', borderSpacing: 0 }}>
          <Thead>
            {table.getHeaderGroups().map((headerGroup) => (
              <Tr key={headerGroup.id}>
                {headerGroup.headers.map((header, index) => {
                  const columnCanSort = header.column.getCanSort();
                  return (
                    <Th
                      key={header.id}
                      textTransform="capitalize"
                      letterSpacing="normal"
                      padding="8px 16px 8px 16px"
                      position="sticky"
                      top="0"
                      borderBottomWidth="thin"
                      style={fixedColumnsStyleToColumnIndexMap[index]}
                      whiteSpace="nowrap"
                    >
                      {header.isPlaceholder ? null : (
                        <Flex alignItems="center">
                          <Box
                            cursor={columnCanSort ? 'pointer' : 'default'}
                            onClick={header.column.getToggleSortingHandler()}
                            mr="8px"
                          >
                            {{
                              asc: <Icon icon="sort-up" />,
                              desc: <Icon icon="sort-down" />,
                            }[header.column.getIsSorted() as string] ??
                              (columnCanSort ? <Icon icon="sort" /> : null)}
                          </Box>

                          <Box mr="8px">
                            {flexRender(
                              header.column.columnDef.header,
                              header.getContext(),
                            )}
                          </Box>
                        </Flex>
                      )}
                    </Th>
                  );
                })}
              </Tr>
            ))}
          </Thead>
          <Tbody>
            {table.getRowModel().rows.map((row) => (
              <TableRow key={row.id} row={row} />
            ))}
          </Tbody>
        </Table>
      </Box>
    </Flex>
  );
};
